import { ChevronsUpDown, Loader2 } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { TableSearchFilterEmptyTableRow } from '@/components/ui/TableSearchFilterEmptyState'
import { ReportTableScroll } from './ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import type { ReportDataTableProps } from '../types/components'

export function ReportDataTable<Row, SortKey extends string = string>({
  rows,
  columns,
  loading = false,
  orderBy,
  sortBy = 'desc',
  rowKey,
  onSort,
  footer,
  rowClassName,
  wrapperClassName,
  tableClassName,
  stickyScrollbar = true,
  testIdPrefix = 'report',
}: ReportDataTableProps<Row, SortKey>) {
  if (loading) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center py-16">
        <Loader2
          className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
          aria-hidden
        />
      </div>
    )
  }

  return (
    <ReportTableScroll
      className={wrapperClassName}
      enabled={stickyScrollbar && rows.length > 0}
      testIdPrefix={testIdPrefix}
    >
      <table
        className={cn(
          reportStyles.listingTable,
          tableClassName,
        )}
      >
        <thead>
          <tr>
            {columns.map((column) => {
              const active =
                column.sortKey != null && orderBy === column.sortKey
              const isRight = column.headerClassName?.includes('text-right')
              const isCenter = column.headerClassName?.includes('text-center')
              return (
                <th
                  key={column.id}
                  className={cn(
                    isRight
                      ? '!text-right'
                      : isCenter
                        ? '!text-center'
                        : 'text-left',
                    column.headerClassName,
                  )}
                >
                  {column.sortKey && onSort ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.sortKey as SortKey)}
                      className={cn(
                        'inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-inherit hover:text-slate-900',
                        isRight
                          ? 'w-full justify-end text-right'
                          : isCenter
                            ? 'w-full justify-center text-center'
                            : 'text-left',
                        active && 'text-[rgb(21_151_255)]',
                      )}
                    >
                      <span className="min-w-0">{column.label}</span>
                      <span
                        className={cn(
                          'inline-flex items-center ml-1 text-[11px]',
                          active ? 'text-[#1597ff]' : 'text-slate-400',
                        )}
                      >
                        {active ? (
                          sortBy === 'asc' ? '▲' : '▼'
                        ) : (
                          <ChevronsUpDown
                            className="h-3 w-3 shrink-0"
                            aria-hidden
                          />
                        )}
                      </span>
                    </button>
                  ) : (
                    <span
                      className={cn(
                        'whitespace-nowrap font-semibold text-inherit',
                        isRight && 'block text-right',
                        isCenter && 'block text-center',
                      )}
                    >
                      {column.label}
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <TableSearchFilterEmptyTableRow
              colSpan={columns.length}
              rowDataTestId={`${testIdPrefix}-table-empty-row`}
              data-testid={`${testIdPrefix}-table-empty-message`}
            />
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                className={cn(
                  'border-b border-slate-100',
                  rowClassName?.(row, index),
                )}
              >
                {columns.map((column) => (
                  <td key={column.id} className={column.className}>
                    {column.render(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
          {footer}
        </tbody>
      </table>
    </ReportTableScroll>
  )
}

export type { ReportDataTableColumn, ReportDataTableProps } from '../types/components'
